// subparts/TimetableEntryCard.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { GlobalChipSelector, ChipOption } from '../../../../components/GlobalComponents/GlobalChipSelector';
import { GlobalCheckboxes, CheckboxOption } from '../../../../components/GlobalComponents/GlobalCheckboxes';
import { GlobalInputField } from '../../../../components/GlobalComponents/GlobalInputField';
import { GlobalButton } from '../../../../components/GlobalComponents/GlobalButton';
import apiClient from '../../../../stateManagers/services/axiosInterceptor';
import { hasNoSubject, hasOptionalTeacher, formatSlotTypeLabel } from './timetableSlotTypes';
import { useSchoolDetailsStore } from '../../../../stateManagers/hooks/useSchoolDetailsStore';

interface TimetableEntry {
  id: string;
  timeSlotId: string;
  subjectId: string;
  teacherAssignmentId: string;
  daysOfWeek: string[];
  roomNumber: string;
}

interface TimetableEntryCardProps {
  entry: TimetableEntry;
  index: number;
  classId: string;
  section: string | null;
  canRemove: boolean;
  timeSlotOptions: ChipOption[];
  subjectOptions: ChipOption[];
  timeSlotsLoading: boolean;
  subjectsLoading: boolean;
  timeSlotsError?: string | null;
  subjectsError?: string | null;
  canShowEntries: boolean;
  loading: boolean;
  occupiedDays?: string[];
  slotType?: string | null;
  onUpdate: (
    id: string,
    field: 'timeSlotId' | 'subjectId' | 'teacherAssignmentId' | 'roomNumber',
    value: string
  ) => void;
  onUpdateDays: (id: string, days: string[]) => void;
  onRemove: (id: string) => void;
}

// Stores both the assignment id and the actual teacher_id.
// For optional-teacher slots (no subject), value === teacherId since there's no assignment row.
interface TeacherOption extends ChipOption {
  teacherId: string;
}

const SELECT_ALL_VALUE = '__SELECT_ALL__';

const TimetableEntryCard: React.FC<TimetableEntryCardProps> = ({
  entry,
  index,
  classId,
  section,
  canRemove,
  timeSlotOptions,
  subjectOptions,
  timeSlotsLoading,
  subjectsLoading,
  timeSlotsError = null,
  subjectsError = null,
  canShowEntries,
  loading,
  occupiedDays = [],
  slotType = null,
  onUpdate,
  onUpdateDays,
  onRemove,
}) => {
  const toast = useRef<Toast>(null);

  const [teacherOptions, setTeacherOptions] = useState<TeacherOption[]>([]);
  const [teachersLoading, setTeachersLoading] = useState(false);

  // Bumped after an inline assign / unassign to force the assigned-teacher refetch.
  const [assignmentsVersion, setAssignmentsVersion] = useState(0);

  // ── Inline assign state ──
  const [assignPanelOpen, setAssignPanelOpen] = useState(false);
  const [allTeachers, setAllTeachers] = useState<ChipOption[]>([]);
  const [allTeachersLoading, setAllTeachersLoading] = useState(false);
  const [teacherToAssign, setTeacherToAssign] = useState('');
  const [assigning, setAssigning] = useState(false);

  // ── Inline unassign state ──
  const [unassignTarget, setUnassignTarget] = useState<TeacherOption | null>(null);
  const [usageChecking, setUsageChecking] = useState(false);
  const [usageCount, setUsageCount] = useState(0);
  const [usageEntries, setUsageEntries] = useState<any[]>([]);
  const [unassigning, setUnassigning] = useState(false);

  // Current session id, sourced from the school details store (single source of truth —
  // avoids hitting a stale/previous session's assignments).
  const currentSessionId = useSchoolDetailsStore(
    (state) => state.schoolDetails?.current_session?.id ?? null
  );
  const currentSessionLabel = useSchoolDetailsStore(
    (state) => state.schoolDetails?.current_session?.session ?? null
  );

  const noSubject = hasNoSubject(slotType);
  const optionalTeacher = hasOptionalTeacher(slotType);
  // Strict non-teaching: no subject AND no teacher allowed (i.e. 'break')
  const noTeacherAtAll = noSubject && !optionalTeacher;

  // No time slots exist at all for this timetable type — the rest of the
  // entry (subject/teacher/days/room) is meaningless without one, so lock it down.
  const noTimeSlotsAvailable = canShowEntries && !timeSlotsLoading && timeSlotOptions.length === 0;

  const baseDayOptions: CheckboxOption[] = [
    { value: 'Monday',    label: 'Monday',    disabled: occupiedDays.includes('Monday')    },
    { value: 'Tuesday',   label: 'Tuesday',   disabled: occupiedDays.includes('Tuesday')   },
    { value: 'Wednesday', label: 'Wednesday', disabled: occupiedDays.includes('Wednesday') },
    { value: 'Thursday',  label: 'Thursday',  disabled: occupiedDays.includes('Thursday')  },
    { value: 'Friday',    label: 'Friday',    disabled: occupiedDays.includes('Friday')    },
    { value: 'Saturday',  label: 'Saturday',  disabled: occupiedDays.includes('Saturday')  },
  ];

  const selectableDayValues = baseDayOptions.filter((d) => !d.disabled).map((d) => d.value);
  const allDaysSelected =
    selectableDayValues.length > 0 &&
    selectableDayValues.every((d) => entry.daysOfWeek.includes(d));

  // "Select All" rendered as the 7th option in the SAME checkbox list, right after Saturday.
  const dayOptions: CheckboxOption[] = [
    ...baseDayOptions,
    {
      value: SELECT_ALL_VALUE,
      label: 'Select All',
      disabled: loading || noTimeSlotsAvailable || selectableDayValues.length === 0,
    },
  ];

  const checkboxValue = allDaysSelected ? [...entry.daysOfWeek, SELECT_ALL_VALUE] : entry.daysOfWeek;

  const handleDaysChange = (values: string[]) => {
    const toggledSelectAll = values.includes(SELECT_ALL_VALUE) && !allDaysSelected;
    const toggledOffSelectAll = !values.includes(SELECT_ALL_VALUE) && allDaysSelected;

    if (toggledSelectAll) {
      onUpdateDays(entry.id, selectableDayValues);
      return;
    }
    if (toggledOffSelectAll) {
      onUpdateDays(entry.id, []);
      return;
    }
    onUpdateDays(entry.id, values.filter((v) => v !== SELECT_ALL_VALUE));
  };

  useEffect(() => {
    const fetchTeachers = async () => {
      // No time slots at all, or strict non-teaching slot (break): no teacher dropdown
      if (noTimeSlotsAvailable || noTeacherAtAll) {
        setTeacherOptions([]);
        return;
      }

      // Optional-teacher slots (power_nap / workbook / activities): fetch ALL teachers, no subject scoping
      if (optionalTeacher) {
        setTeachersLoading(true);
        try {
          const response = await apiClient.get('teachers-list2', {
            params: { page: 1, limit: 100 },
          });

          if (response.data.status === 'success') {
            const list = response.data.data ?? [];
            const options: TeacherOption[] = list
              .map((t: any) => ({
                value: t.id.toString(),
                label: t.name,
                teacherId: t.id.toString(),
              }))
              .sort((a: TeacherOption, b: TeacherOption) => a.label.localeCompare(b.label));

            setTeacherOptions(options);
          } else {
            setTeacherOptions([]);
          }
        } catch (error) {
          console.error('Error fetching teachers list:', error);
          setTeacherOptions([]);
        } finally {
          setTeachersLoading(false);
        }
        return;
      }

      // Normal teaching slot: subject-scoped assigned teachers
      if (!entry.subjectId || !classId) {
        setTeacherOptions([]);
        return;
      }

      // Backend requires session_id — without it we can't safely scope
      // assignments to the current session, so bail out rather than risk
      // showing teachers assigned in a previous session.
      if (!currentSessionId) {
        console.warn('No current session id available — skipping assigned teachers fetch');
        setTeacherOptions([]);
        return;
      }

      setTeachersLoading(true);
      try {
        const sectionParam = section ?? '';
        // Matches Go route: GET /api/assigned-teachers/class/:class_id/section/:section/subject/:subject_id
        const endpoint = `/api/assigned-teachers/class/${classId}/section/${sectionParam}/subject/${entry.subjectId}`;
        const response = await apiClient.get(endpoint, {
          params: { session_id: currentSessionId },
        });

        if (response.data.status === 'success') {
          const teachersArray = response.data.assignments ?? response.data.data ?? [];

          const options: TeacherOption[] = teachersArray
            .map((item: any) => ({
              // value = assignment row id (used as key in chip)
              value: item.id.toString(),
              label: `${item.teacher_name} (${item.subject_name})`,
              // teacherId = actual teacher_id for the API payload
              teacherId: item.teacher_id.toString(),
            }))
            .sort((a: TeacherOption, b: TeacherOption) =>
              a.label.localeCompare(b.label)
            );

          setTeacherOptions(options);
        } else {
          setTeacherOptions([]);
        }
      } catch (error) {
        console.error('Error fetching teachers:', error);
        setTeacherOptions([]);
      } finally {
        setTeachersLoading(false);
      }
    };

    fetchTeachers();
  }, [entry.subjectId, classId, section, noTimeSlotsAvailable, noTeacherAtAll, optionalTeacher, currentSessionId, assignmentsVersion]);

  /* ══════════════════════════════════════════════════════════════════
     HYBRID ASSIGN / UNASSIGN (normal teaching slots only)
     - Assign: creates the missing sp_assigned_teachers row inline, then
       re-runs the subject-scoped fetch above.
     - Unassign: first asks the backend how many timetable rows depend on
       this assignment. Removing the assignment while timetable entries
       still point at it is exactly the orphan case we must not create,
       so the cascade is explicit (force=true) and deletes those rows.
  ══════════════════════════════════════════════════════════════════ */

  const notify = (severity: 'success' | 'error' | 'warn' | 'info', summary: string, detail: string) =>
    toast.current?.show({ severity, summary, detail, life: 5000 });

  const canManageAssignments =
    !noSubject && !!classId && !!entry.subjectId && !!currentSessionId && !noTimeSlotsAvailable;

  const fetchAllTeachers = useCallback(async () => {
    if (allTeachers.length > 0) return;
    setAllTeachersLoading(true);
    try {
      // Same source as the Assign Teachers modal — keep these in sync.
      const response = await apiClient.get('/api/assigned-teachers/teachers-with-assignments', {
        params: { page: 1, limit: 100 },
      });
      if (response.data.status === 'success') {
        const list = (response.data.data ?? [])
          .map((t: any) => ({
            value: String(t.id || t._id || ''),
            label: t.name || '',
          }))
          .filter((t: ChipOption) => !!t.value)
          .sort((a: ChipOption, b: ChipOption) => a.label.localeCompare(b.label));
        setAllTeachers(list);
      } else {
        setAllTeachers([]);
      }
    } catch (e) {
      console.error('Error fetching teachers list:', e);
      setAllTeachers([]);
    } finally {
      setAllTeachersLoading(false);
    }
  }, [allTeachers.length]);

  const openAssignPanel = () => {
    setTeacherToAssign('');
    setAssignPanelOpen(true);
    fetchAllTeachers();
  };

  const handleAssign = async () => {
    if (!teacherToAssign || !currentSessionId || !currentSessionLabel) return;

    setAssigning(true);
    try {
      const response = await apiClient.post('/api/assigned-teachers/assign', {
        session_id: currentSessionId,
        session: currentSessionLabel,
        class_id: classId,
        sections: section ? [section] : [''],
        teacher_id: teacherToAssign,
        subject_id: entry.subjectId,
      });

      if (response.data.status === 'success') {
        notify('success', 'Teacher Assigned', 'Teacher is now assigned to this class and subject.');
        setAssignPanelOpen(false);
        setTeacherToAssign('');
        setAssignmentsVersion((v) => v + 1);
        // Select the freshly assigned teacher for this entry.
        onUpdate(entry.id, 'teacherAssignmentId', teacherToAssign);
      } else {
        notify('error', 'Assign Failed', response.data.message || 'Could not assign teacher.');
      }
    } catch (err: any) {
      notify('error', 'Assign Failed', err.response?.data?.message || 'Could not assign teacher.');
    } finally {
      setAssigning(false);
    }
  };

  const openUnassign = async (target: TeacherOption) => {
    setUnassignTarget(target);
    setUsageCount(0);
    setUsageEntries([]);
    setUsageChecking(true);
    try {
      const response = await apiClient.get('/api/assigned-teachers/timetable-usage', {
        params: {
          class_id: classId,
          subject_id: entry.subjectId,
          teacher_id: target.teacherId,
          session_id: currentSessionId,
          ...(section ? { section } : {}),
        },
      });
      if (response.data.status === 'success') {
        setUsageCount(response.data.count ?? 0);
        setUsageEntries(response.data.entries ?? []);
      }
    } catch (err: any) {
      notify('error', 'Check Failed', err.response?.data?.message || 'Could not check timetable usage.');
      setUnassignTarget(null);
    } finally {
      setUsageChecking(false);
    }
  };

  const handleUnassign = async () => {
    if (!unassignTarget || !currentSessionId) return;

    setUnassigning(true);
    try {
      const response = await apiClient.delete('/api/assigned-teachers/unassign', {
        data: {
          class_id: classId,
          section_name: section ?? '',
          teacher_id: unassignTarget.teacherId,
          subject_id: entry.subjectId,
          session_id: currentSessionId,
          // Explicit cascade: delete the dependent timetable rows shown in the dialog.
          force: usageCount > 0,
        },
      });

      if (response.data.status === 'success') {
        const removed = response.data.deleted_timetable_entries ?? 0;
        notify(
          'success',
          'Teacher Unassigned',
          removed > 0
            ? `Teacher unassigned. ${removed} dependent timetable ${removed === 1 ? 'entry' : 'entries'} removed.`
            : 'Teacher unassigned successfully.',
        );
        if (entry.teacherAssignmentId === unassignTarget.teacherId) {
          onUpdate(entry.id, 'teacherAssignmentId', '');
        }
        setUnassignTarget(null);
        setAssignmentsVersion((v) => v + 1);
      } else {
        notify('error', 'Unassign Failed', response.data.message || 'Could not unassign teacher.');
      }
    } catch (err: any) {
      notify('error', 'Unassign Failed', err.response?.data?.message || 'Could not unassign teacher.');
    } finally {
      setUnassigning(false);
    }
  };

  // When chip selection changes, store id AND notify parent with real teacher_id
  // (for optionalTeacher slots, value === teacherId directly; for normal slots, value is the assignment id)
  const handleTeacherChange = (selectedValue: string) => {
    if (!selectedValue) {
      onUpdate(entry.id, 'teacherAssignmentId', '');
      return;
    }
    const selected = teacherOptions.find((t) => t.value === selectedValue);
    if (selected) {
      onUpdate(entry.id, 'teacherAssignmentId', selected.teacherId);
    }
  };

  // Reverse-map stored teacher_id back to chip display value
  const chipDisplayValue =
    entry.teacherAssignmentId
      ? teacherOptions.find((t) => t.teacherId === entry.teacherAssignmentId)?.value ?? ''
      : '';

  const showTeacherSelector = !noTimeSlotsAvailable && (optionalTeacher || (!noSubject && !!entry.subjectId));

  /* ── Empty-state messages, derived from real state — not guessed ── */

  const timeSlotEmptyMessage = !canShowEntries
    ? 'Complete previous fields first'
    : timeSlotsError
      ? timeSlotsError
      : 'No time slots configured for this timetable type';

  const subjectEmptyMessage = !classId
    ? 'Select class first'
    : subjectsError
      ? subjectsError
      : 'No subjects available for this class';

  const teacherEmptyMessage = (!optionalTeacher && !entry.subjectId)
    ? 'Select subject first'
    : !optionalTeacher && !currentSessionId
      ? 'Unable to determine current session'
      : optionalTeacher
        ? 'No teachers found'
        : 'No teachers assigned for this subject';

  const selectedTeacherOption =
    teacherOptions.find((t) => t.teacherId === entry.teacherAssignmentId) ?? null;

  return (
    <>
    <Toast ref={toast} position="top-right" />

    <div className="entry-card">
      <div className="entry-number">Entry {index + 1}</div>

      {canRemove && (
        <button
          className="remove-button"
          onClick={() => onRemove(entry.id)}
          disabled={loading}
        >
          <i className="pi pi-times" style={{ fontSize: '11px' }} />
        </button>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '8px' }}>
        <GlobalChipSelector
          label="Time Slot"
          icon="pi pi-clock"
          value={entry.timeSlotId}
          onChange={(v) => onUpdate(entry.id, 'timeSlotId', v)}
          options={timeSlotOptions}
          required
          loading={timeSlotsLoading}
          disabled={loading || !canShowEntries}
          emptyMessage={timeSlotEmptyMessage}
        />

        {noTimeSlotsAvailable && (
          <div className="info-note">
            <i className="pi pi-info-circle" />
            No time slots are configured for this timetable type — add one before filling out the rest of this entry
          </div>
        )}

        {!noSubject && (
          <GlobalChipSelector
            label="Subject"
            icon="pi pi-book"
            value={entry.subjectId}
            onChange={(v) => {
              onUpdate(entry.id, 'subjectId', v);
              // Clear teacher when subject changes — teacher fetch re-runs via useEffect
              onUpdate(entry.id, 'teacherAssignmentId', '');
            }}
            options={subjectOptions}
            required
            loading={subjectsLoading}
            disabled={loading || !classId || noTimeSlotsAvailable}
            emptyMessage={subjectEmptyMessage}
          />
        )}

        {noTeacherAtAll && !noTimeSlotsAvailable && (
          <div className="info-note">
            <i className="pi pi-info-circle" />
            This is a {formatSlotTypeLabel(slotType || '')} slot — no subject or teacher needed
          </div>
        )}

        {showTeacherSelector && (
          <GlobalChipSelector
            label={optionalTeacher ? 'Supervising Teacher (optional)' : 'Assigned Teacher'}
            icon="pi pi-user"
            value={chipDisplayValue}
            onChange={handleTeacherChange}
            options={teacherOptions}
            required={!optionalTeacher}
            loading={teachersLoading}
            disabled={loading || (teacherOptions.length === 0 && !optionalTeacher)}
            allowDeselect
            emptyMessage={teacherEmptyMessage}
          />
        )}

        {/* ── Hybrid assign / unassign controls (teaching slots only) ── */}
        {canManageAssignments && (
          <div className="assignment-tools">
            {!teachersLoading && teacherOptions.length === 0 && (
              <div className="info-note" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', color: '#92400e' }}>
                <i className="pi pi-exclamation-triangle" />
                No teacher is assigned to this subject{section ? ` for section ${section}` : ''} — assign one below without leaving this modal.
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="button"
                className="assign-link-button"
                onClick={() => (assignPanelOpen ? setAssignPanelOpen(false) : openAssignPanel())}
                disabled={loading || assigning}
              >
                <i className={assignPanelOpen ? 'pi pi-times' : 'pi pi-user-plus'} />
                {assignPanelOpen
                  ? 'Cancel'
                  : teacherOptions.length === 0
                    ? 'Assign a teacher to this subject'
                    : 'Assign another teacher'}
              </button>

              {selectedTeacherOption && (
                <button
                  type="button"
                  className="unassign-link-button"
                  onClick={() => openUnassign(selectedTeacherOption)}
                  disabled={loading || usageChecking || unassigning}
                >
                  <i className="pi pi-user-minus" />
                  {usageChecking ? 'Checking…' : 'Unassign selected teacher'}
                </button>
              )}
            </div>

            {assignPanelOpen && (
              <div className="assign-panel">
                <GlobalChipSelector
                  label="Select Teacher to Assign"
                  icon="pi pi-user-plus"
                  value={teacherToAssign}
                  onChange={setTeacherToAssign}
                  options={allTeachers}
                  loading={allTeachersLoading}
                  disabled={assigning}
                  allowDeselect
                  emptyMessage="No teachers found in this school"
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                  <GlobalButton
                    label={assigning ? 'Assigning…' : 'Assign Teacher'}
                    icon="pi pi-check"
                    onClick={handleAssign}
                    variant="primary"
                    size="medium"
                    disabled={!teacherToAssign || assigning || !currentSessionLabel}
                    loading={assigning}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="days-checkbox-row">
          <style>{`
            .days-checkbox-row label:has(input[value="${SELECT_ALL_VALUE}"]),
            .days-checkbox-row div:has(> input[value="${SELECT_ALL_VALUE}"]) {
              background: #eff6ff;
              border-radius: 6px;
              padding: 4px 8px;
            }
          `}</style>
          <GlobalCheckboxes
            label="Days of Week"
            options={dayOptions}
            value={checkboxValue}
            onChange={handleDaysChange}
            required
            layout="horizontal"
            gap={16}
            disabled={loading || noTimeSlotsAvailable}
          />
        </div>

        <GlobalInputField
          label="Room Number"
          value={entry.roomNumber}
          onChange={(e) => onUpdate(entry.id, 'roomNumber', e.target.value)}
          placeholder="e.g. 101, Lab-3, Auditorium"
          disabled={loading || noTimeSlotsAvailable}
          icon="pi pi-home"
        />
      </div>

      <style>{`
        .assignment-tools {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .assign-link-button,
        .unassign-link-button {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .assign-link-button {
          background: rgba(59,130,246,0.08);
          border: 1px dashed rgba(59,130,246,0.45);
          color: #1e40af;
        }
        .assign-link-button:hover:not(:disabled) { background: rgba(59,130,246,0.16); }
        .unassign-link-button {
          background: rgba(239,68,68,0.08);
          border: 1px solid rgba(239,68,68,0.3);
          color: #dc2626;
        }
        .unassign-link-button:hover:not(:disabled) { background: rgba(239,68,68,0.16); }
        .assign-link-button:disabled,
        .unassign-link-button:disabled { opacity: 0.55; cursor: not-allowed; }
        .assign-panel {
          background: rgba(59,130,246,0.04);
          border: 1px solid rgba(59,130,246,0.2);
          border-radius: 12px;
          padding: 14px;
        }
      `}</style>
    </div>

    {/* ── Unassign confirmation — shows timetable dependencies before cascading ── */}
    <Dialog
      visible={!!unassignTarget}
      onHide={() => !unassigning && setUnassignTarget(null)}
      style={{ width: '90vw', maxWidth: '460px', borderRadius: '20px', overflow: 'hidden' }}
      showHeader={false}
      contentStyle={{ padding: '26px', borderRadius: '20px' }}
      modal
      dismissableMask={!unassigning}
      draggable={false}
      resizable={false}
    >
      {unassignTarget && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '46px', height: '46px', borderRadius: '50%',
              background: usageCount > 0 ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <i
                className={usageCount > 0 ? 'pi pi-exclamation-triangle' : 'pi pi-user-minus'}
                style={{ fontSize: '19px', color: usageCount > 0 ? '#d97706' : '#dc2626' }}
              />
            </div>
            <div style={{ fontSize: '16.5px', fontWeight: 700, color: '#1e293b' }}>
              {usageCount > 0 ? 'Timetable entries depend on this' : 'Unassign teacher?'}
            </div>
          </div>

          <div style={{ fontSize: '13.5px', color: '#475569', lineHeight: 1.6 }}>
            {usageCount > 0 ? (
              <>
                <strong>{unassignTarget.label}</strong> is still used by{' '}
                <strong>{usageCount}</strong> timetable {usageCount === 1 ? 'entry' : 'entries'} in this
                session. Unassigning will <strong>permanently delete</strong> those entries so no timetable
                row is left pointing at a removed assignment.
              </>
            ) : (
              <>
                This removes <strong>{unassignTarget.label}</strong> from this class and subject
                {section ? ` (section ${section})` : ''}. No timetable entries depend on it.
              </>
            )}
          </div>

          {usageEntries.length > 0 && (
            <div style={{
              maxHeight: '160px', overflowY: 'auto', background: '#f8fafc',
              border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px 12px',
            }}>
              {usageEntries.map((u: any, i: number) => (
                <div key={i} style={{ fontSize: '12.5px', color: '#475569', padding: '3px 0' }}>
                  <strong style={{ color: '#1e40af' }}>{u.period_label || 'Period'}</strong>
                  {u.section ? ` • Section ${u.section}` : ''}
                  {Array.isArray(u.day_of_week) && u.day_of_week.length > 0
                    ? ` • ${u.day_of_week.join(', ')}`
                    : ''}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
            <GlobalButton
              label="Cancel"
              onClick={() => setUnassignTarget(null)}
              variant="secondary"
              size="medium"
              disabled={unassigning}
              fullWidth
            />
            <GlobalButton
              label={
                unassigning
                  ? 'Removing…'
                  : usageCount > 0
                    ? `Unassign & Delete ${usageCount}`
                    : 'Unassign'
              }
              onClick={handleUnassign}
              variant="danger"
              size="medium"
              disabled={unassigning}
              loading={unassigning}
              fullWidth
            />
          </div>
        </div>
      )}
    </Dialog>
    </>
  );
};

export default TimetableEntryCard;